import type { MembershipRole, MembershipSubjectType, Profile } from 'models';

const hasMembership = ({
  role,
  subjectId,
  subjectType,
  viewer,
}: {
  role?: MembershipRole;
  subjectId?: string;
  subjectType: MembershipSubjectType;
  viewer: Profile;
}) =>
  viewer.memberships.some(
    (membership) =>
      membership.subjectType === subjectType &&
      (!role || membership.role === role) &&
      (!subjectId || membership.subjectId === subjectId),
  );

const hasRoleInSubject = ({
  role,
  subjectType,
  viewer,
}: {
  role: MembershipRole;
  subjectType: MembershipSubjectType;
  viewer: Profile;
}): boolean => hasMembership({ role, subjectType, viewer });

const hasRoleInSomeSubjectProfile = ({
  role,
  subjectId,
  subjectType,
  viewer,
}: {
  role: MembershipRole;
  subjectId: string;
  subjectType: MembershipSubjectType;
  viewer: Profile;
}): boolean => hasMembership({ role, subjectId, subjectType, viewer });

export const isCommunitiesAdmin = ({ viewer }: { viewer: Profile }) =>
  hasRoleInSubject({ role: 'admin', subjectType: 'Community', viewer });

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion -- We need assertion to boolean, there was problem with radnom null
export const isSuperAdmin = ({ viewer }: { viewer: Profile }) => Boolean(viewer.superadmin);

export const isOrganizationsAdmin = ({ viewer }: { viewer: Profile }) =>
  hasRoleInSubject({ role: 'admin', subjectType: 'Organization', viewer });

export const isProgramsAdmin = ({ viewer }: { viewer: Profile }) =>
  hasRoleInSubject({ role: 'admin', subjectType: 'Program', viewer });

export const isOrganizationAdminProfile = ({ organizationId, viewer }: { organizationId: string; viewer: Profile }) =>
  hasRoleInSomeSubjectProfile({ role: 'admin', subjectId: organizationId, subjectType: 'Organization', viewer });

export const isCommunityAdminProfile = ({ communityId, viewer }: { communityId: string; viewer: Profile }) =>
  hasRoleInSomeSubjectProfile({ role: 'admin', subjectId: communityId, subjectType: 'Community', viewer });

export const isCommunityOrOrganizationAdmin = ({
  community,
  viewer,
}: {
  community: { id: string; organizationId: string };
  viewer: Profile;
}) =>
  isCommunityAdminProfile({ communityId: community.id, viewer }) ||
  isOrganizationAdminProfile({ organizationId: community.organizationId, viewer });
